/**
 * Copyright(c) 2019 Knut Petter Meen, all rights reserved.
 */
package net.scalytica.blaargh.pages

import japgolly.scalajs.react._
import japgolly.scalajs.react.vdom.html_<^._
import net.scalytica.blaargh.CssSettings._
import net.scalytica.blaargh.models.Config
import net.scalytica.blaargh.styles.BlaarghBootstrapCSS
import net.scalytica.blaargh.utils.StringUtils
import org.scalajs.dom.ext.Ajax

import scala.concurrent.Future
import scala.concurrent.ExecutionContext.Implicits.global
import scalacss.ScalaCssReact._

object AboutPage {

  case class Props(siteConf: Future[Config])

  case class State(conf: Config, content: Option[String])

  object Styles extends StyleSheet.Inline {

    import dsl._

    val aboutCard = style("blaargh-about-card")(
      BlaarghBootstrapCSS.card,
      padding(5 px)
    )

    val profileCard = style("blaargh-profile-card")(
      BlaarghBootstrapCSS.card,
      paddingLeft(5 px),
      paddingRight(5 px),
      paddingBottom(5 px),
      paddingTop(15 px),
      addClassName("text-xs-center")
    )

    val centeredAvatar = style("blaarg-profile-avatar")(
      BlaarghBootstrapCSS.Mixins.imgRounded,
      BlaarghBootstrapCSS.Mixins.centerBlock,
      height(120 px)
    )

    val authorSocial = style("blaargh-author-social")(
      fontSize(1.2 em),
      marginBottom(5 px),
      color.black,
      unsafeChild(".fa")(
        marginRight(5 px)
      ),
      transitionProperty := "font-size",
      BlaarghBootstrapCSS.Mixins.easeOutAnimation,
      &.hover(
        fontSize(1.5 em)
      )
    )
  }

  class Backend($ : BackendScope[Props, State]) {
    def init: Callback = $.props.map { p =>
      Callback
        .future[Unit] {
          for {
            config <- p.siteConf
            page   <- loadPage
          } yield {
            $.modState(_.copy(conf = config, content = page))
          }
        }
        .runNow()
    }

    def loadPage: Future[Option[String]] =
      Ajax.get(url = "pages/about.html").map { xhr =>
        xhr.status match {
          case ok: Int if ok == 200 =>
            Some(xhr.responseText)

          case ko: Int =>
            Callback.warn(s"Request didn't go well. Returned status code $ko")
            None
        }
      }

    // scalastyle:off method.length magic.number
    def render(props: Props, state: State) = {
      <.div(^.key := "about-1",
        ^.className := "about",
        ^.id := Views.About.basePath.stripPrefix("#"),
        <.div(^.key := "about-1-1",
          BlaarghBootstrapCSS.container,
          <.div(^.key := "about-1-1-1",
            BlaarghBootstrapCSS.container,
            ^.paddingTop := "4em",
            <.div(^.key := "about-1-1-1-1",
              BlaarghBootstrapCSS.row,
              <.div(^.key := "about-1-1-1-1-1",
                BlaarghBootstrapCSS.col(4),
                <.div(^.key := "about-1-1-1-1-1-1",
                  Styles.profileCard,
                  <.img(
                    Styles.centeredAvatar,
                    ^.src := "assets/images/profile_pic.jpg"
                  ),
                  <.div(^.key := "about-1-1-1-1-1-1-1",
                    BlaarghBootstrapCSS.cardBlock,
                    <.h4(BlaarghBootstrapCSS.cardTitle, state.conf.owner.name),
                    <.p(BlaarghBootstrapCSS.cardText, state.conf.owner.bio),
                    <.p(
                      BlaarghBootstrapCSS.cardText,
                      StringUtils
                        .asOption(state.conf.owner.email)
                        .map { email =>
                          <.a(
                            Styles.authorSocial,
                            ^.href := s"mailto:$email",
                            <.i(^.className := "fa fa-envelope")
                          )
                        }
                        .getOrElse(EmptyVdom),
                      StringUtils
                        .asOption(state.conf.owner.twitter)
                        .map { twitter =>
                          <.a(
                            Styles.authorSocial,
                            ^.href := s"https://twitter.com/$twitter",
                            ^.target := "_blank",
                            <.i(^.className := "fa fa-twitter")
                          )
                        }
                        .getOrElse(EmptyVdom),
                      StringUtils
                        .asOption(state.conf.owner.facebook)
                        .map { facebook =>
                          <.a(
                            Styles.authorSocial,
                            ^.href := s"https://facebook.com/$facebook",
                            ^.target := "_blank",
                            <.i(^.className := "fa fa-facebook")
                          )
                        }
                        .getOrElse(EmptyVdom),
                      StringUtils
                        .asOption(state.conf.owner.instagram)
                        .map { instagram =>
                          <.a(
                            Styles.authorSocial,
                            ^.href := s"https://www.instagram.com/$instagram",
                            ^.target := "_blank",
                            <.i(^.className := "fa fa-instagram")
                          )
                        }
                        .getOrElse(EmptyVdom),
                      StringUtils
                        .asOption(state.conf.owner.linkedin)
                        .map { linkedin =>
                          <.a(
                            Styles.authorSocial,
                            ^.href := s"https://www.instagram.com/$linkedin",
                            ^.target := "_blank",
                            <.i(^.className := "fa fa-linkedin")
                          )
                        }
                        .getOrElse(EmptyVdom)
                    )
                  )
                )
              ),
              <.div(^.key := "about-1-1-1-1-2",
                BlaarghBootstrapCSS.col(8),
                <.div(^.key := "about-1-1-1-1-2-1",
                  Styles.aboutCard,
                  <.div(^.key := "about-1-1-1-1-2-1-1",
                    BlaarghBootstrapCSS.cardBlock,
                    ^.dangerouslySetInnerHtml := state.content
                      .map(identity)
                      .getOrElse("")
                  )
                )
              )
            )
          )
        )
      )
    }
    // scalastyle:on method.length magic.number
  }

  val component = ScalaComponent
    .builder[Props]("About")
    .initialStateFromProps(_ => State(Config.empty, None))
    .renderBackend[Backend]
    .componentDidMount(_.backend.init)
    .build

  def apply(conf: Future[Config]) = component(Props(conf))

}



// WEBPACK FOOTER //
// /Users/kpm/Development/scalytica/renslebraten.no/_project/src/main/scala/net/scalytica/blaargh/pages/AboutPage.scala